import dayjs from 'dayjs';
import type { DateValue } from '@/kit/DateField';
import {
  ReportTypeCode,
  type AdjustedTrialBalanceSource,
  type ReportDetail,
  type TrialBalanceOption,
} from '@/core/models/reports.models';

/**
 * The reactive form of Add New Report (Angular `reportForm`) as plain data, with the rules Angular ran on value
 * changes. Every function returns a new state (it never modifies the one it is given).
 */

/** The controls, with the values `fb.group` gave them. */
export interface FormValues {
  refNo: string | null;
  description: string | null;
  /** Fixed by the list this form was opened from; never rendered. */
  reportType: number | null;
  /** 0 Full, 1 Abridged, 2 Filleted (Companies House) - "Report Type" on screen. */
  accountsVariant: number | null;
  currentTrialBalance: string | null;
  showComparative: boolean | null;
  previousTrialBalance: string | null;
  directorSignDate: DateValue;
  accountantSignDate: DateValue;
  letterOfEngagement: DateValue;
  clientApprovalDate: DateValue;
  /**
   * Not persisted. APT246: Auto (the default) runs the validation gate and switches on the accounting policies the trial
   * balance gives figures for, as 3.0's "auto check notes" did; Manual leaves the notes as they are and skips the gate.
   */
  relatedNotesCheck: string | null;
  /**
   * APT342: the accounting period whose Adjusted Trial Balance is the source of the figures; null - the Source trial balance, the
   * default and what every existing report uses. Choosing a period sets the current trial balance to that period's own
   * (`chooseAdjustedSource`); the API refuses a period that has no adjusted trial balance.
   */
  adjustedPeriod: string | null;
}

export type FieldName = keyof FormValues;

/** Control order of the form group: `reset()` visits them in this order, running the value-change rules as it goes. */
export const FIELD_ORDER: FieldName[] = [
  'refNo',
  'description',
  'reportType',
  'accountsVariant',
  'currentTrialBalance',
  'showComparative',
  'previousTrialBalance',
  'directorSignDate',
  'accountantSignDate',
  'letterOfEngagement',
  'clientApprovalDate',
  'relatedNotesCheck',
  'adjustedPeriod',
];

// BUG-048: the description is optional, as in the 3.0 app. So are the Director and Accountant Sign Dates: 3.0
// Reports/Default.js:139-140 validates only their format (`required` is commented out), and the form pre-fills them with
// today, so a cleared date is allowed and sent as null.
const REQUIRED: FieldName[] = [
  'refNo',
  'reportType',
  'accountsVariant',
  'currentTrialBalance',
];

export interface FormState {
  v: FormValues;
  /** The previous trial balance is required only while comparative figures are shown. */
  previousRequired: boolean;
  touched: FieldName[];
  /** #175: the trial balances offered for this report type (plus the saved report's own). */
  trialBalances: TrialBalanceOption[];
  /** Every offered trial balance except the current one (recomputed when the current one changes). */
  previousTrialBalances: TrialBalanceOption[];
  loaded: ReportDetail | null;
  nextRefNo: string;
  initialType: number;
  /** #174: set when a saved report's trial balance has since been deleted, so the preparer picks another. */
  deletedTrialBalanceNote: string | null;
  /** APT342: the periods the API says have an adjusted trial balance (the saved report's own period included). */
  adjustedSources: AdjustedTrialBalanceSource[];
  /**
   * APT342 (client, 28 Oct 10:52: "just show option to choose between source TB or ATB and let client choose the TB for the period they
   * want"): which of the two choices of the Source of Figures field is on. It is held apart from `v.adjustedPeriod` because the Adjusted
   * choice can be on before its period is chosen (several periods have one and nothing is picked silently).
   */
  sourceKind: SourceKind;
}

/** The Source of Figures choices: the trial balance as saved, or a period's Adjusted Trial Balance. */
export type SourceKind = 'source' | 'adjusted';

export function initialFormState(initialType: number): FormState {
  return {
    v: {
      refNo: '',
      description: '',
      reportType: null,
      accountsVariant: 0,
      currentTrialBalance: null,
      showComparative: false,
      previousTrialBalance: null,
      directorSignDate: null,
      accountantSignDate: null,
      letterOfEngagement: null,
      clientApprovalDate: null,
      relatedNotesCheck: 'auto',
      adjustedPeriod: null,
    },
    previousRequired: false,
    touched: [],
    trialBalances: [],
    previousTrialBalances: [],
    loaded: null,
    nextRefNo: '',
    initialType,
    deletedTrialBalanceNote: null,
    adjustedSources: [],
    sourceKind: 'source',
  };
}

const clone = (state: FormState): FormState => ({ ...state, v: { ...state.v }, touched: [...state.touched] });

// The rules below mutate the state they are handed; the exported functions hand them a private clone.

/** A trial balance date as the API lists it ('01-Apr-2024', or ISO), as a sortable day; null when it cannot be read. */
function listedDate(value: string | null | undefined): number | null {
  const slashed = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(slashDate(value));
  return slashed ? Date.UTC(+slashed[3], +slashed[2] - 1, +slashed[1]) : null;
}

/**
 * Whether `candidate` can give the comparative figures for `current`: its period ends before the current one starts
 * (the server's ComparativeRules.IsEarlier). When either date is unknown the order cannot be checked, so it is allowed.
 */
function endsBefore(candidate: TrialBalanceOption, current: TrialBalanceOption | undefined): boolean {
  const end = listedDate(candidate.toDate);
  const start = listedDate(current?.fromDate);
  return end === null || start === null || end < start;
}

/**
 * The trial balances that can be the previous one: not the current one, and from an earlier period. The one already
 * chosen stays listed, so a report saved before this rule still opens as it was saved (the server then explains).
 */
function updatePreviousOptions(s: FormState): void {
  const current = s.trialBalances.find((t) => t.id === s.v.currentTrialBalance);
  s.previousTrialBalances = s.trialBalances.filter(
    (t) => t.id !== s.v.currentTrialBalance && (endsBefore(t, current) || t.id === s.v.previousTrialBalance),
  );
}

/**
 * Defaults the previous trial balance to the latest one from an earlier period (the list is newest first). A trial
 * balance from the same or a later period is never suggested: the server refuses it for the comparative figures.
 */
function suggestPrevious(s: FormState): void {
  const current = s.v.currentTrialBalance;
  const currentRow = s.trialBalances.find((t) => t.id === current);
  if (s.v.previousTrialBalance === current) {
    s.v.previousTrialBalance = null;
  }
  if (!s.v.showComparative || s.v.previousTrialBalance || !current) {
    return;
  }
  const index = s.trialBalances.findIndex((t) => t.id === current);
  const earlier = index >= 0 ? s.trialBalances.slice(index + 1).find((t) => endsBefore(t, currentRow)) : undefined;
  if (earlier) {
    s.v.previousTrialBalance = earlier.id;
  }
}

function applyComparative(s: FormState, show: boolean): void {
  s.previousRequired = show;
  if (show) {
    suggestPrevious(s);
  }
}

/** `setValue` of one control: stores the value and runs the subscriptions Angular had on two of them. */
function setControlIn<K extends FieldName>(s: FormState, name: K, value: FormValues[K]): void {
  s.v[name] = value;
  if (name === 'currentTrialBalance') {
    // Choosing another current trial balance drops a previous one that is not from an earlier period than it.
    const currentRow = s.trialBalances.find((t) => t.id === value);
    const chosen = s.trialBalances.find((t) => t.id === s.v.previousTrialBalance);
    if (chosen && !endsBefore(chosen, currentRow)) {
      s.v.previousTrialBalance = null;
    }
    updatePreviousOptions(s);
    suggestPrevious(s);
  } else if (name === 'showComparative') {
    applyComparative(s, !!value);
  } else if (name === 'previousTrialBalance') {
    updatePreviousOptions(s);
  }
}

export function setControl<K extends FieldName>(state: FormState, name: K, value: FormValues[K]): FormState {
  const s = clone(state);
  setControlIn(s, name, value);
  // The note about a deleted trial balance is answered once the preparer chooses a trial balance in its place (the
  // suggestion made on loading is not their choice).
  if ((name === 'currentTrialBalance' || name === 'previousTrialBalance') && value) {
    s.deletedTrialBalanceNote = null;
  }
  return s;
}

/** `FormGroup.reset(values)`: controls missing from `values` become null; the form becomes untouched. */
function resetIn(s: FormState, values: Partial<FormValues>): void {
  FIELD_ORDER.forEach((name) => setControlIn(s, name, (values[name] ?? null) as never));
  s.touched = [];
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A trial balance date (31-Mar-2026 from the API, or 2026-03-31) as dd/MM/yyyy; anything else is returned as given. */
export function slashDate(value: string | null | undefined): string {
  const text = (value ?? '').trim();
  const named = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/.exec(text);
  if (named) {
    const month = MONTHS.findIndex((m) => m.toLowerCase() === named[2].toLowerCase());
    if (month >= 0) return `${named[1].padStart(2, '0')}/${String(month + 1).padStart(2, '0')}/${named[3]}`;
  }
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  return iso ? `${iso[3]}/${iso[2]}/${iso[1]}` : text;
}

/**
 * BUG-048: the trial balance picker names each one as the 3.0 app did - ref, description and period, e.g.
 * "TB-02 - Year end (01/04/2025 - 31/03/2026)". Parts that are blank are left out.
 */
export function trialBalanceLabel(t: TrialBalanceOption): string {
  const description = (t.description ?? '').trim();
  const from = slashDate(t.fromDate);
  const to = slashDate(t.toDate);
  return `${t.refNo}${description ? ` - ${description}` : ''}${from && to ? ` (${from} - ${to})` : ''}`;
}

/** Stores the loaded trial balances and report (#175) and fills the form: `onCancel()` after the forkJoin. */
export function withLoadedData(
  state: FormState,
  data: {
    trialBalances: TrialBalanceOption[];
    nextRefNo: string;
    detail: ReportDetail | null;
    adjustedSources?: AdjustedTrialBalanceSource[];
  },
): FormState {
  const s = clone(state);
  // #175 / BUG-054: a trial balance carries its type (0 Annual, 1 Management only, same codes as the report type). An
  // annual report is offered annual trial balances only; a management report is offered every trial balance, as in the
  // 3.0 app, so one built from the annual trial balance is possible. The report's own saved trial balances stay listed
  // when editing, so an older report still opens as it was saved.
  // As the old application (TrialBalanceMaster.GetInsertedPeriod / GetInsertedPrevPeriod), only a trial balance that
  // balances is offered, as the current or the previous one; the report's own stay listed here too (the server then
  // refuses to save one that does not balance, and says so).
  const own = [data.detail?.currentTrialBalanceId, data.detail?.previousTrialBalanceId];
  s.trialBalances = data.trialBalances
    .filter((t) => s.initialType === ReportTypeCode.Management || t.typeId === s.initialType || own.includes(t.id))
    .filter((t) => t.isBalanced !== false || own.includes(t.id))
    .map((t) => ({ ...t, label: trialBalanceLabel(t) }));
  s.nextRefNo = data.nextRefNo;
  s.loaded = data.detail;
  s.adjustedSources = withSavedSource(data.adjustedSources ?? [], data.detail);
  updatePreviousOptions(s);
  return populate(s);
}

/**
 * APT342: a report saved from a period's Adjusted Trial Balance keeps showing it as its source when the form is opened again,
 * even if the API no longer lists that period (its adjusted balances were cleared): the preparer then sees what the report used
 * and the API explains when it is saved again.
 */
function withSavedSource(
  sources: AdjustedTrialBalanceSource[],
  detail: ReportDetail | null,
): AdjustedTrialBalanceSource[] {
  const periodId = detail?.balanceSource === 1 ? detail.sourceAccountingPeriodId : null;
  if (!detail || !periodId || sources.some((x) => x.accountingPeriodId === periodId)) return sources;
  return [
    ...sources,
    {
      accountingPeriodId: periodId,
      label: 'Adjusted trial balance (no longer available for this period)',
      fromDate: '',
      toDate: '',
      trialBalanceId: detail.currentTrialBalanceId,
      trialBalanceRef: '',
      accountCount: 0,
      journalCount: 0,
    },
  ];
}

/**
 * APT342: the periods the Source of figures field offers. Only those whose trial balance the Current Trial Balance field offers
 * (`offeredTrialBalanceIds` - a locked period's is not offered, nor is one of another type), so choosing one always shows its
 * trial balance there; the one already chosen, or the one a saved report used, stays offered.
 */
export function offeredAdjustedSources(
  state: FormState,
  offeredTrialBalanceIds: ReadonlySet<string>,
): AdjustedTrialBalanceSource[] {
  const own = state.loaded?.balanceSource === 1 ? state.loaded.sourceAccountingPeriodId : null;
  return state.adjustedSources.filter(
    (x) =>
      offeredTrialBalanceIds.has(x.trialBalanceId) ||
      x.accountingPeriodId === state.v.adjustedPeriod ||
      x.accountingPeriodId === own,
  );
}

/**
 * APT342: "Source of figures" - null the Source trial balance (the figures of the trial balance chosen), a period's id its Adjusted
 * Trial Balance. The period's own trial balance becomes the current one (it supplies the report's reference and dates), the
 * previous-trial-balance rules running as for any other choice of current trial balance. Never run on its own: nothing is chosen
 * until the preparer chooses it.
 */
export function chooseAdjustedSource(state: FormState, periodId: string | null): FormState {
  const s = clone(state);
  const source = periodId ? s.adjustedSources.find((x) => x.accountingPeriodId === periodId) : undefined;
  if (!source) {
    s.v.adjustedPeriod = null;
    s.sourceKind = 'source';
    return s;
  }
  s.v.adjustedPeriod = source.accountingPeriodId;
  s.sourceKind = 'adjusted';
  setControlIn(s, 'currentTrialBalance', source.trialBalanceId);
  s.deletedTrialBalanceNote = null;
  return s;
}

/**
 * APT342 (client, 28 Oct 10:52): the first selector of the Source of Figures - the Source trial balance, or the Adjusted trial balance.
 * Source: the trial balance list below works as it always did (the trial balance stays as it is). Adjusted: the period's own adjusted trial
 * balance is chosen in the second selector; it is chosen here only when exactly one period offers one, otherwise nothing is chosen - the
 * trial balance is cleared so no trial balance of the Source choice is left standing for it - and Generate asks for it. An adjusted trial
 * balance of another period is never taken for the user.
 */
export function chooseFiguresSource(
  state: FormState,
  kind: SourceKind,
  offeredTrialBalanceIds: ReadonlySet<string>,
): FormState {
  if (kind === 'source') return chooseAdjustedSource(state, null);
  if (state.sourceKind === 'adjusted') return state;

  const choices = offeredAdjustedSources(state, offeredTrialBalanceIds);
  if (choices.length === 0) return state; // no period has one: the Adjusted choice is not available
  if (choices.length === 1) return chooseAdjustedSource(state, choices[0].accountingPeriodId);

  const s = clone(state);
  s.sourceKind = 'adjusted';
  s.v.adjustedPeriod = null;
  setControlIn(s, 'currentTrialBalance', null);
  s.deletedTrialBalanceNote = null;
  return s;
}

/** APT342: what the report is saved with - 1 for the Adjusted Trial Balance, 0 for the trial balance as saved. */
export const balanceSourceOf = (values: Pick<FormValues, 'adjustedPeriod'>): 0 | 1 => (values.adjustedPeriod ? 1 : 0);

/** "Cancel Changes", also the initial population. */
export function cancelChanges(state: FormState): FormState {
  return populate(clone(state));
}

function populate(s: FormState): FormState {
  s.deletedTrialBalanceNote = null;
  // APT342: a saved report opens on the choice it was generated from; every other report starts on the Source trial balance.
  s.sourceKind = s.loaded?.balanceSource === 1 ? 'adjusted' : 'source';
  if (s.loaded) {
    const r = s.loaded;
    // #174: deleted trial balances are never offered; a report saved against one opens with the field empty and says
    // why, rather than holding an id the list cannot show.
    const offered = new Set(s.trialBalances.map((t) => t.id));
    const current = offered.has(r.currentTrialBalanceId) ? r.currentTrialBalanceId : null;
    const previous =
      r.previousTrialBalanceId && offered.has(r.previousTrialBalanceId) ? r.previousTrialBalanceId : null;
    if (!current) {
      s.deletedTrialBalanceNote =
        'The trial balance this report was generated from has been deleted. Choose another trial balance and generate again.';
    } else if (r.showComparative && r.previousTrialBalanceId && !previous) {
      s.deletedTrialBalanceNote =
        'The previous-year trial balance used for the comparative figures has been deleted. Choose another one and generate again.';
    }
    resetIn(s, {
      refNo: r.refNo,
      description: r.description,
      reportType: r.reportType,
      accountsVariant: r.accountsVariant,
      currentTrialBalance: current,
      showComparative: r.showComparative,
      previousTrialBalance: previous,
      directorSignDate: r.directorSignDate,
      accountantSignDate: r.accountantSignDate,
      letterOfEngagement: r.letterOfEngagementDate,
      clientApprovalDate: r.clientApprovalDate,
      // APT246: Related Notes opens on Auto (the reset would otherwise clear it, leaving neither option selected).
      relatedNotesCheck: 'auto',
      // APT342: a saved report shows what its figures came from; every other report is the Source trial balance.
      adjustedPeriod: r.balanceSource === 1 ? (r.sourceAccountingPeriodId ?? null) : null,
    });
  } else {
    // Defaults to today, matching how these have always been filed; still editable before saving.
    const today = dayjs();
    resetIn(s, {
      refNo: s.nextRefNo,
      reportType: s.initialType,
      accountsVariant: 0,
      // BUG-048: a new report shows comparative figures unless the preparer unticks it, as in the 3.0 app.
      showComparative: true,
      directorSignDate: today,
      accountantSignDate: today,
      letterOfEngagement: today,
      clientApprovalDate: today,
      relatedNotesCheck: 'auto',
    });
  }
  applyComparative(s, !!s.v.showComparative);
  return s;
}

/** The report was saved: "Cancel Changes" now resets to the saved values. */
export function withSavedReport(state: FormState, detail: ReportDetail): FormState {
  return { ...state, loaded: detail };
}

/**
 * TC 86488: the current trial balance is not offered from an accounting period that is locked - for every report type, as
 * the old application's list of current trial balances left locked periods out (TrialBalanceMaster.GetInsertedPeriod; its
 * previous-year list did not, so the comparative one is still offered from a locked period). The one a saved report was
 * generated from stays listed so it still opens as saved.
 */
export function unlockedTrialBalances(
  trialBalances: TrialBalanceOption[],
  lockedPeriodIds: ReadonlySet<string>,
  keepId?: string | null,
): TrialBalanceOption[] {
  return trialBalances.filter(
    (t) => t.id === keepId || !t.accountingPeriodId || !lockedPeriodIds.has(t.accountingPeriodId),
  );
}

/** BUG-048: the old application's Ref. No. limit ("AA-1, max 10"). */
export const REF_NO_MAX_LENGTH = 10;

/**
 * BUG-048: a Ref. No. over the limit. A report saved before the limit keeps its longer reference unchanged (the server
 * allows it); only a new or changed reference is held to it.
 */
export const isRefNoTooLong = (state: FormState): boolean => {
  const refNo = (state.v.refNo ?? '').trim();
  return refNo.length > REF_NO_MAX_LENGTH && refNo !== state.loaded?.refNo;
};

/** BUG-048: the longest Ref. No. the field takes - the limit, or a longer reference the opened report was saved with. */
export const refNoMaxLength = (state: FormState): number =>
  Math.max(REF_NO_MAX_LENGTH, state.loaded?.refNo.length ?? 0);

const isEmpty = (value: unknown) =>
  value === null || value === undefined || (typeof value === 'string' && value.length === 0);

/** The required controls this client does not need (a sole trader's Director Sign Date, say): one, or several. */
export type NotRequired = FieldName | readonly FieldName[] | undefined;

const isExempt = (name: FieldName, notRequired: NotRequired): boolean =>
  Array.isArray(notRequired) ? notRequired.includes(name) : name === notRequired;

/** Angular `Validators.required` (previous trial balance only while comparative figures are shown). */
export function isInvalid(state: FormState, name: FieldName, notRequired?: NotRequired): boolean {
  if (isExempt(name, notRequired)) return false;
  if (name === 'refNo' && isRefNoTooLong(state)) return true;
  return (
    (REQUIRED.includes(name) || (name === 'previousTrialBalance' && state.previousRequired)) && isEmpty(state.v[name])
  );
}

export const isFormInvalid = (state: FormState, notRequired?: NotRequired): boolean =>
  FIELD_ORDER.some((name) => isInvalid(state, name, notRequired));

/** `markAllAsTouched()`. */
export const touchAll = (state: FormState): FormState => ({ ...state, touched: [...FIELD_ORDER] });

export const touch = (state: FormState, name: FieldName): FormState =>
  state.touched.includes(name) ? state : { ...state, touched: [...state.touched, name] };

/** Angular `ng-invalid ng-touched`. */
export const showInvalid = (state: FormState, name: FieldName, notRequired?: NotRequired): boolean =>
  state.touched.includes(name) && isInvalid(state, name, notRequired);

/** BUG-048: what each required control says under itself once it is touched and empty. */
// BUG-048 (QA round 2): in the old application's wording ("Please select Current Trial Balance").
export const FIELD_MESSAGES: Partial<Record<FieldName, string>> = {
  refNo: 'Please provide Ref. No.',
  reportType: 'Please select Report Type',
  accountsVariant: 'Please select Report Type',
  currentTrialBalance: 'Please select Current Trial Balance',
  previousTrialBalance: 'Please select Previous Trial Balance',
};

/** An LLP's members, not directors, approve its accounts: the old application labelled the Director Sign Date "Members appr. date" for an LLP (titled "Members date of approval"). */
export const LLP_DIRECTOR_SIGN_DATE_LABEL = 'Members appr. date';
export const LLP_DIRECTOR_SIGN_DATE_TITLE = 'Members date of approval';

/** Oct-10 #169 (99063): the old application's message for an Accounts Type (new / revised accounts) not chosen. */
export const ACCOUNTS_TYPE_MESSAGE = 'Please select Accounts Type';

/** Oct-10 #169: the old application names the previous trial balance before the current one. */
const MESSAGE_ORDER: FieldName[] = FIELD_ORDER.filter((name) => name !== 'previousTrialBalance').flatMap((name) =>
  name === 'currentTrialBalance' ? (['previousTrialBalance', name] as FieldName[]) : [name],
);

/**
 * BUG-048 (QA round 2): Generate with fields missing names each of them, as the old application did, in one toast (one
 * shows at a time). Oct-10 #169 (99063): every missing field is named, the previous trial balance too, in the old
 * application's order ("Please select Previous Trial Balance", then "Please select Current Trial Balance").
 */
export function missingFieldsMessage(
  state: FormState,
  notRequired?: NotRequired,
  messageOf: Partial<Record<FieldName, string>> = FIELD_MESSAGES,
): string | null {
  const messages: string[] = [];
  for (const name of MESSAGE_ORDER) {
    if (!isInvalid(state, name, notRequired)) continue;
    const message =
      name === 'refNo' && isRefNoTooLong(state)
        ? `The Ref. No. can be at most ${REF_NO_MAX_LENGTH} characters`
        : requiredMessage(state, name, messageOf);
    if (!messages.includes(message)) messages.push(message);
  }
  return messages.length === 0 ? null : messages.map((m) => (m.endsWith('.') ? m : `${m}.`)).join(' ');
}

/** BUG-048: the message to show under a control, or null while it is valid or untouched. */
export const fieldError = (
  state: FormState,
  name: FieldName,
  notRequired?: NotRequired,
  messageOf: Partial<Record<FieldName, string>> = FIELD_MESSAGES,
): string | null => {
  if (!showInvalid(state, name, notRequired)) return null;
  if (name === 'refNo' && isRefNoTooLong(state)) return `The Ref. No. can be at most ${REF_NO_MAX_LENGTH} characters`;
  return requiredMessage(state, name, messageOf);
};

/** APT342: with the Adjusted choice on, the trial balance field is the Adjusted Trial Balance selector, so it is named as that. */
export const ADJUSTED_TRIAL_BALANCE_MESSAGE = 'Please select Adjusted Trial Balance';

function requiredMessage(state: FormState, name: FieldName, messageOf: Partial<Record<FieldName, string>>): string {
  if (name === 'currentTrialBalance' && state.sourceKind === 'adjusted') return ADJUSTED_TRIAL_BALANCE_MESSAGE;
  return messageOf[name] ?? 'This field is required';
}
